import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { BASE_URL } from "apiUtil";

interface UseSocketReturn {
  progress: number | null;
  stepId: string | null;
  isDone: boolean
}

interface ProcessingUpdate {
  projID: string;
  stepID: any;
  percentComplete?: number | null;
}

const useSocket = (projectId: string): UseSocketReturn => {
  const [progress, setProgress] = useState<number | null>(null);
  const [stepId, setStepId] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [isDone, setIsDone] = useState(false);
  
  useEffect(() => {
    // Mount the socket connection on component mount
    const socket = io(`${BASE_URL}/`, {
      reconnection: true, // Enable reconnections
      reconnectionAttempts: 5, // Max reconnection attempts
      reconnectionDelay: 1000, // Delay between reconnection attempts (ms)
      transports: ["websocket"], // Explicitly use WebSocket
    });

    socketRef.current = socket;

    console.log("Connecting to Socket.IO...");

    socket.on("connect", () => console.log("Connected to server."));
    socket.on("disconnect", () => console.log("Disconnected from server."));

    // Cleanup on unmount
    return () => {
      if (socket) {
        console.log("Disconnecting from Socket.IO...");
        socket.disconnect();
      }
    };
  }, []); // Socket connection established only on mount

  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    // Handle processing updates
    const handleProcessingUpdate = (data: ProcessingUpdate) => {
      if (data.projID === projectId) {
        const {stepID, percentComplete} = data;
        setProgress(percentComplete || 3);
        setStepId(stepID);
      }
    };

    // Handle processing done
    const handleProcessingDone = (data: { projID: string }) => {
      if (data.projID === projectId) {
        console.log(`Processing done: Project ${data.projID}`, data);
        setProgress(100); // Assume 100% progress when done
        setStepId("3"); // Clear step ID when done
        setIsDone(true)
      }
    };

    // Register event listeners
    socket.on("processing_update", handleProcessingUpdate);
    socket.on("processing_done", handleProcessingDone);
    socket.on("processing_error", (data: { projID: string; error: string }) => {
      if (data.projID === projectId) {
        console.error(
          `Processing error: Project ${data.projID}, Error: ${data.error}`
        );
        setProgress(null); // Reset progress on error
        setStepId(null); // Clear step ID on error
      }
    });

    // Cleanup on dependency change
    return () => {
      socket.off("processing_update", handleProcessingUpdate);
      socket.off("processing_done", handleProcessingDone);
    };
  }, [projectId]); // Update listeners when projectId changes
  console.log(progress, stepId, isDone )
  return { progress, stepId, isDone };
};

export default useSocket;
